import HttpTransport from '@/bundles/Http/transport/HttpTransport';
import ErrorMapper from '@/bundles/Http/mappers/ErrorMapper';
import RoleToMapper from '@/bundles/Roles/mappers/RoleToMapper';
import McpRoleMapper from '@/bundles/Roles/mappers/McpRoleMapper';
import McpPermissionMapper from '@/bundles/Roles/mappers/McpPermissionMapper';
import Role from '@/bundles/Roles/models/Role';
import McpRole from '@/bundles/Roles/models/McpRole';
import McpPermission from '@/bundles/Roles/models/McpPermission';
import { IMcpRoleDto } from '@/bundles/Roles/dto';
import { IMcpRoleError } from '@/bundles/Roles/types';
import { ICustomError } from '@/bundles/Http/interfaces/ICustomError';

export default class RoleService {
  private readonly transport: HttpTransport;
  private readonly errorMapper: ErrorMapper;
  private readonly roleMapper: RoleToMapper;
  private readonly mcpRoleMapper: McpRoleMapper;
  private readonly mcpPermissionMapper: McpPermissionMapper;

  constructor ({
    transport,
    errorMapper,
    roleMapper,
    mcpRoleMapper,
    mcpPermissionMapper,
  }: {
    transport: HttpTransport;
    errorMapper: ErrorMapper;
    roleMapper: RoleToMapper;
    mcpRoleMapper: McpRoleMapper;
    mcpPermissionMapper: McpPermissionMapper;
  }) {
    this.transport = transport;
    this.errorMapper = errorMapper;
    this.roleMapper = roleMapper;
    this.mcpRoleMapper = mcpRoleMapper;
    this.mcpPermissionMapper = mcpPermissionMapper;
  }

  async list (): Promise<Role[]> {
    try {
      const response = await this.transport.get('/role');
      return response.map((data: any) => this.roleMapper.mapToModel(data));
    } catch (error) {
      throw this.errorMapper.mapToCodeModel(error);
    }
  }

  async listMcp (): Promise<McpRole[]> {
    try {
      const response = await this.transport.get('/role', { type: 'mcp' });
      return response.map((data: any) => this.mcpRoleMapper.mapToModel(data));
    } catch (error) {
      throw this.mapMcpError(error);
    }
  }

  async listUserMcp (userId: number): Promise<McpRole[]> {
    try {
      const response = await this.transport.get(`/user/${ userId }/mcp-roles`);
      return response.map((data: any) => this.mcpRoleMapper.mapToModel(data));
    } catch (error) {
      throw this.mapMcpError(error);
    }
  }

  async listMcpPermissions (): Promise<McpPermission[]> {
    try {
      const response = await this.transport.get('/role/mcp/permissions');
      return response.map((data: any) => this.mcpPermissionMapper.mapToModel(data));
    } catch (error) {
      throw this.mapMcpError(error);
    }
  }

  async createMcp (dto: IMcpRoleDto): Promise<McpRole> {
    try {
      const response = await this.transport.post('/role/mcp', dto);
      return this.mcpRoleMapper.mapToModel(response);
    } catch (error) {
      throw this.mapMcpError(error);
    }
  }

  async updateMcp (id: number, dto: IMcpRoleDto): Promise<McpRole> {
    try {
      const response = await this.transport.put(`/role/mcp/${id}`, dto);
      return this.mcpRoleMapper.mapToModel(response);
    } catch (error) {
      throw this.mapMcpError(error);
    }
  }

  async deleteMcp (id: number): Promise<void> {
    try {
      await this.transport.delete(`/role/mcp/${id}`);
    } catch (error) {
      throw this.mapMcpError(error);
    }
  }

  private mapMcpError (error: any): IMcpRoleError {
    const data = error?.response?.data ?? {};
    const code: string | undefined = data.error;
    const userCount: number | undefined = data.userCount;

    try {
      const mapped = this.errorMapper.mapToCodeModel(error);
      return { ...mapped, code, userCount };
    } catch (mapped) {
      return { ...(mapped as ICustomError), code, userCount };
    }
  }
}
